import { createTheme } from '@mui/material/styles';

/**
 * Neumorphic theme — cream background + dark teal accent + soft shadow depth.
 * Inspired by the QMS Vault mockup.
 *
 * Parallel to themes/index.js — the classic theme is untouched.
 * App.js picks one based on customization.themeMode.
 */

const NEU = {
    bg: '#E8EBE9',
    bgSoft: '#EEF1EF',
    bgDeep: '#DDE1DF',
    primaryMain: '#0F5E5C',
    primaryDark: '#0A4744',
    primaryLight: '#3A8987',
    textPrimary: '#1F2A2A',
    textSecondary: '#5B6B6B',
    textMuted: '#8B9595',
    divider: '#D5D9D7',
    success: '#1F8A4C',
    warning: '#E89034',
    error: '#D44A4A',
    info: '#2E78D2',
    shadowLight: '#FFFFFF',
    shadowDark: 'rgba(154, 167, 165, 0.55)'
};

// Re-used shadow recipes (Neumorphism = outer light TL + outer dark BR)
const shadowRaised = `8px 8px 18px ${NEU.shadowDark}, -8px -8px 18px ${NEU.shadowLight}`;
const shadowRaisedSm = `4px 4px 10px ${NEU.shadowDark}, -4px -4px 10px ${NEU.shadowLight}`;
const shadowInset = `inset 4px 4px 8px ${NEU.shadowDark}, inset -4px -4px 8px ${NEU.shadowLight}`;
const shadowHover = `10px 10px 22px ${NEU.shadowDark}, -10px -10px 22px ${NEU.shadowLight}`;

export const themeNeumorphic = (customization) => {
    const fontFamily =
        customization?.fontFamily ||
        '"Noto Sans Lao", "Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

    const palette = {
        mode: 'light',
        primary: {
            light: NEU.primaryLight,
            main: NEU.primaryMain,
            dark: NEU.primaryDark,
            200: NEU.primaryLight,
            800: NEU.primaryDark,
            contrastText: '#FFFFFF'
        },
        secondary: {
            light: NEU.bgSoft,
            main: NEU.primaryMain,
            dark: NEU.primaryDark,
            200: NEU.primaryLight,
            800: NEU.primaryDark,
            contrastText: '#FFFFFF'
        },
        success: { light: '#D9F0E4', main: NEU.success, dark: '#15683A', 200: '#A7DDBE', contrastText: '#FFFFFF' },
        warning: { light: '#FCE4CC', main: NEU.warning, dark: '#B96E18', contrastText: '#FFFFFF' },
        error: { light: '#F7D7D7', main: NEU.error, dark: '#A02E2E', contrastText: '#FFFFFF' },
        info: { light: '#D6E6FA', main: NEU.info, dark: '#1E5BA5', contrastText: '#FFFFFF' },
        orange: { light: '#FCE4CC', main: NEU.warning, dark: '#B96E18' },
        dark: {
            light: NEU.textSecondary,
            main: NEU.textPrimary,
            dark: '#0B1414',
            800: NEU.textPrimary,
            900: '#0B1414'
        },
        grey: {
            50: NEU.bgSoft,
            100: NEU.bg,
            200: NEU.divider,
            300: NEU.divider,
            500: NEU.textSecondary,
            600: NEU.textSecondary,
            700: NEU.textPrimary,
            900: NEU.textPrimary
        },
        background: {
            default: NEU.bg,
            paper: NEU.bgSoft
        },
        // Product brand tokens — kept identical to classic theme so content colors
        // stay consistent across themes. Use theme.palette.brand.* in components.
        brand: {
            orange: '#f57224',
            blue: '#0c4da2',
            indigo: '#3035BC',
            green: '#2e7d32',
            greenDark: '#1b5e20',
            red: '#c62828',
            pink: '#e11d48'
        },
        text: {
            primary: NEU.textPrimary,
            secondary: NEU.textSecondary,
            dark: NEU.textPrimary,
            hint: NEU.textMuted
        },
        divider: NEU.divider
    };

    const typography = {
        fontFamily,
        h6: { fontWeight: 600, color: NEU.textPrimary, fontSize: '0.8125rem' },
        h5: { fontWeight: 600, color: NEU.textPrimary, fontSize: '0.9375rem' },
        h4: { fontWeight: 700, color: NEU.textPrimary, fontSize: '1.0625rem' },
        h3: { fontWeight: 700, color: NEU.textPrimary, fontSize: '1.375rem', letterSpacing: '-0.01em' },
        h2: { fontWeight: 800, color: NEU.textPrimary, fontSize: '1.75rem', letterSpacing: '-0.015em' },
        h1: { fontWeight: 800, color: NEU.textPrimary, fontSize: '2.25rem', letterSpacing: '-0.02em' },
        subtitle1: { fontSize: '0.875rem', fontWeight: 600, color: NEU.textPrimary },
        subtitle2: { fontSize: '0.8125rem', fontWeight: 500, color: NEU.textSecondary },
        caption: { fontSize: '0.75rem', color: NEU.textMuted, fontWeight: 500 },
        body1: { fontSize: '0.875rem', color: NEU.textPrimary, fontWeight: 400 },
        body2: { fontSize: '0.8125rem', color: NEU.textSecondary, fontWeight: 400, lineHeight: 1.55 },
        button: { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
        customInput: {
            marginTop: 1,
            marginBottom: 1,
            '& > label': { top: 23, left: 0, color: NEU.textSecondary, '&[data-shrink="false"]': { top: 5 } },
            '& > div > input': { padding: '30.5px 14px 11.5px !important' },
            '& legend': { display: 'none' },
            '& fieldset': { top: 0 }
        },
        mainContent: {
            backgroundColor: NEU.bg,
            width: '100%',
            minHeight: 'calc(100vh - 88px)',
            flexGrow: 1,
            padding: '24px',
            marginTop: '88px',
            marginRight: '20px',
            borderRadius: '12px'
        },
        menuCaption: { fontSize: '0.875rem', fontWeight: 700, color: NEU.textPrimary, padding: '6px', textTransform: 'capitalize', marginTop: '10px' },
        subMenuCaption: { fontSize: '0.6875rem', fontWeight: 500, color: NEU.textMuted, textTransform: 'capitalize' },
        commonAvatar: { cursor: 'pointer', borderRadius: '8px' },
        smallAvatar: { width: '22px', height: '22px', fontSize: '1rem' },
        mediumAvatar: { width: '34px', height: '34px', fontSize: '1.2rem' },
        largeAvatar: { width: '44px', height: '44px', fontSize: '1.5rem' }
    };

    const themeOptions = {
        direction: 'ltr',
        palette,
        mixins: {
            toolbar: {
                minHeight: '48px',
                padding: '16px',
                '@media (min-width: 600px)': { minHeight: '48px' }
            }
        },
        typography
    };

    const themes = createTheme(themeOptions);

    themes.components = {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: NEU.bg,
                    color: NEU.textPrimary
                },
                // Neumorphic spacing — only widen Card breathing room so shadows aren't clipped.
                '.MuiCard-root + .MuiCard-root': {
                    marginTop: '10px'
                },
                // MainCard (project-wide) injects a 1px teal border via inline sx. In neumorphic
                // the shadow already separates cards — strip the border to keep the look clean.
                '.MuiCard-root': {
                    border: 'none !important'
                },
                // Strip raised bg/shadow from IconButtons that sit INSIDE inputs (adornments)
                // — high specificity to beat the global MuiIconButton override.
                '.MuiOutlinedInput-root .MuiIconButton-root, .MuiInputAdornment-root .MuiIconButton-root, .MuiInputBase-root .MuiIconButton-root': {
                    backgroundColor: 'transparent !important',
                    boxShadow: 'none !important'
                },
                '.MuiOutlinedInput-root .MuiIconButton-root:hover, .MuiInputAdornment-root .MuiIconButton-root:hover, .MuiInputBase-root .MuiIconButton-root:hover': {
                    backgroundColor: 'rgba(15, 94, 92, 0.08) !important',
                    boxShadow: 'none !important'
                }
            }
        },
        MuiPaper: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    backgroundColor: NEU.bgSoft,
                    borderRadius: 20,
                    boxShadow: shadowRaised
                },
                rounded: { borderRadius: 20 }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: NEU.bgSoft,
                    borderRadius: 20,
                    boxShadow: shadowRaised,
                    border: 'none',
                    margin: '4px 3px'
                }
            }
        },
        MuiCardHeader: {
            styleOverrides: {
                root: { padding: '20px', borderBottom: 'none' },
                title: { fontSize: '1rem', fontWeight: 700, color: NEU.textPrimary }
            }
        },
        MuiCardContent: {
            styleOverrides: { root: { padding: '20px' } }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                    padding: '8px 22px'
                },
                contained: {
                    backgroundColor: NEU.primaryMain,
                    color: '#FFFFFF',
                    boxShadow: shadowRaisedSm,
                    '&:hover': { backgroundColor: NEU.primaryDark, boxShadow: shadowHover }
                },
                outlined: {
                    backgroundColor: NEU.bgSoft,
                    border: 'none',
                    color: NEU.primaryMain,
                    boxShadow: shadowRaisedSm,
                    '&:hover': { border: 'none', backgroundColor: NEU.bgSoft, boxShadow: shadowHover }
                },
                text: { color: NEU.primaryMain, '&:hover': { backgroundColor: 'rgba(15, 94, 92, 0.06)' } }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    // Transparent by default — neumorphic raised look caused overlap with
                    // titles/text. Use the .neu-raised class explicitly when needed.
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: 'rgba(15, 94, 92, 0.08)',
                        boxShadow: 'none'
                    },
                    '&.neu-raised': {
                        backgroundColor: NEU.bgSoft,
                        boxShadow: shadowRaisedSm,
                        '&:hover': { backgroundColor: NEU.bgSoft, boxShadow: shadowHover }
                    }
                }
            }
        },
        MuiButtonGroup: {
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    backgroundColor: NEU.bgSoft,
                    boxShadow: shadowRaisedSm,
                    overflow: 'hidden',
                    border: 'none',
                    // Make ALL buttons inside look like one single pill — same bg, no dividers
                    '& .MuiButton-root, & .MuiButtonGroup-grouped': {
                        backgroundColor: 'transparent',
                        color: NEU.textPrimary,
                        border: 'none !important',
                        borderRadius: 0,
                        boxShadow: 'none',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: 'rgba(15, 94, 92, 0.06)', boxShadow: 'none' }
                    },
                    '& .MuiButton-root:first-of-type': {
                        borderTopLeftRadius: 999,
                        borderBottomLeftRadius: 999,
                        paddingLeft: 20
                    },
                    '& .MuiButton-root:last-of-type': {
                        borderTopRightRadius: 999,
                        borderBottomRightRadius: 999,
                        paddingRight: 14,
                        minWidth: 36
                    },
                    '&:hover': { boxShadow: shadowHover }
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    boxShadow: shadowRaisedSm,
                    border: 'none',
                    fontWeight: 600,
                    // Default (no color prop): cream bg + dark text
                    '&.MuiChip-colorDefault': {
                        backgroundColor: NEU.bgSoft,
                        color: NEU.textPrimary
                    },
                    // FILLED colored variants → bg = palette.main, text = white
                    '&.MuiChip-filled.MuiChip-colorPrimary': { backgroundColor: NEU.primaryMain, color: '#FFFFFF' },
                    '&.MuiChip-filled.MuiChip-colorSecondary': { backgroundColor: NEU.primaryMain, color: '#FFFFFF' },
                    '&.MuiChip-filled.MuiChip-colorSuccess': { backgroundColor: NEU.success, color: '#FFFFFF' },
                    '&.MuiChip-filled.MuiChip-colorError': { backgroundColor: NEU.error, color: '#FFFFFF' },
                    '&.MuiChip-filled.MuiChip-colorWarning': { backgroundColor: NEU.warning, color: '#FFFFFF' },
                    '&.MuiChip-filled.MuiChip-colorInfo': { backgroundColor: NEU.info, color: '#FFFFFF' },
                    // OUTLINED colored variants → light tinted bg + colored text + colored icon (no border, neumorphic shadow)
                    '&.MuiChip-outlined.MuiChip-colorPrimary': {
                        backgroundColor: 'rgba(15, 94, 92, 0.12)',
                        color: NEU.primaryMain,
                        '& .MuiChip-icon': { color: NEU.primaryMain }
                    },
                    '&.MuiChip-outlined.MuiChip-colorSecondary': {
                        backgroundColor: 'rgba(15, 94, 92, 0.12)',
                        color: NEU.primaryMain,
                        '& .MuiChip-icon': { color: NEU.primaryMain }
                    },
                    '&.MuiChip-outlined.MuiChip-colorSuccess': {
                        backgroundColor: 'rgba(31, 138, 76, 0.14)',
                        color: NEU.success,
                        '& .MuiChip-icon': { color: NEU.success }
                    },
                    '&.MuiChip-outlined.MuiChip-colorError': {
                        backgroundColor: 'rgba(212, 74, 74, 0.14)',
                        color: NEU.error,
                        '& .MuiChip-icon': { color: NEU.error }
                    },
                    '&.MuiChip-outlined.MuiChip-colorWarning': {
                        backgroundColor: 'rgba(232, 144, 52, 0.16)',
                        color: NEU.warning,
                        '& .MuiChip-icon': { color: NEU.warning }
                    },
                    '&.MuiChip-outlined.MuiChip-colorInfo': {
                        backgroundColor: 'rgba(46, 120, 210, 0.14)',
                        color: NEU.info,
                        '& .MuiChip-icon': { color: NEU.info }
                    }
                },
                outlined: { border: 'none', boxShadow: shadowRaisedSm }
            }
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: NEU.bg,
                    backgroundImage: 'none',
                    color: NEU.textPrimary,
                    boxShadow: 'none',
                    borderBottom: `1px solid ${NEU.divider}`
                }
            }
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: NEU.bg,
                    borderRight: 'none',
                    boxShadow: 'none'
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: NEU.bg,
                    borderRadius: 14,
                    boxShadow: shadowInset,
                    transition: 'box-shadow 0.15s ease',
                    // Always-transparent fieldset — no border at any state
                    '& fieldset': { borderColor: 'transparent !important', borderWidth: '0 !important' },
                    '&:hover fieldset': { borderColor: 'transparent !important', borderWidth: '0 !important' },
                    '&.Mui-focused fieldset': { borderColor: 'transparent !important', borderWidth: '0 !important' },
                    // Subtle primary-tinted inset shadow on focus (instead of a hard border)
                    '&.Mui-focused': {
                        boxShadow: `${shadowInset}, inset 0 0 0 2px rgba(15, 94, 92, 0.35)`
                    },
                    // Hide the notched legend gap since labels float ABOVE the input pill
                    '& legend': { display: 'none' },
                    '& fieldset > legend': { display: 'none' }
                },
                input: { padding: '12px 14px' }
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                outlined: {
                    transform: 'translate(20px, 14px) scale(1)',
                    color: NEU.textSecondary,
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(8px, -14px) scale(0.8)',
                        background: 'transparent',
                        padding: '0 4px',
                        fontWeight: 600,
                        color: NEU.textSecondary
                    },
                    '&.MuiInputLabel-shrink.Mui-focused': {
                        color: NEU.primaryMain
                    }
                }
            }
        },
        // Give floating labels room to sit above without colliding with the previous field
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginTop: '4px'
                }
            }
        },
        MuiTextField: {
            defaultProps: { variant: 'outlined' }
        },
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    backgroundColor: NEU.bgSoft,
                    borderRadius: 20,
                    boxShadow: shadowRaised
                }
            }
        },
        MuiTableHead: {
            styleOverrides: {
                root: { backgroundColor: 'transparent' }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: { borderBottomColor: NEU.divider },
                head: { fontWeight: 700, color: NEU.textPrimary, fontSize: '0.8125rem' }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: { backgroundColor: NEU.bgSoft, borderRadius: 24, boxShadow: shadowRaised }
            }
        },
        MuiMenu: {
            styleOverrides: {
                paper: { borderRadius: 16, backgroundColor: NEU.bgSoft, boxShadow: shadowRaised }
            }
        },
        MuiDivider: {
            styleOverrides: { root: { borderColor: NEU.divider } }
        },
        MuiAvatar: {
            styleOverrides: {
                root: { backgroundColor: NEU.bgSoft, boxShadow: shadowRaisedSm }
            }
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: { backgroundColor: NEU.textPrimary, color: '#FFFFFF', borderRadius: 8, fontSize: '0.75rem' }
            }
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: { borderRadius: 999, backgroundColor: NEU.bgDeep, height: 8, boxShadow: shadowInset },
                bar: { borderRadius: 999, backgroundColor: NEU.primaryMain }
            }
        },
        // Neumorphic: dialog/modal backdrop is light (white) instead of the dark scrim.
        // The `invisible` variant (Menu/Popover/Autocomplete) is left transparent.
        MuiBackdrop: {
            styleOverrides: {
                root: ({ ownerState }) => ({
                    ...(!ownerState.invisible && { backgroundColor: 'rgba(233, 241, 244, 0.7)' })
                })
            }
        }
    };

    // Expose tokens for components that read directly from theme.palette.neu (optional)
    themes.palette.neu = { ...NEU, shadowRaised, shadowRaisedSm, shadowInset, shadowHover };

    return themes;
};

export default themeNeumorphic;
